import * as React from "react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { REFINED } from "../risikoanalyseStep/tokens";
import { I } from "./kvpIcons";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

/**
 * Wiederverwendbarer kvp-rd-Bestätigungsdialog — ersetzt `window.confirm()` in
 * der Vorgänge-Oberfläche durch einen Modal im Refined-Design (Aubergine/Gold,
 * Hanken Grotesk). Portaliert nach `document.body`, damit er außerhalb des
 * `.kvp-rd`-Scopes sauber über allem liegt; Optik daher via REFINED-Tokens inline.
 *
 * Bedienung: ESC / Backdrop-Klick = Abbrechen; Enter bestätigt (Fokus liegt auf
 * dem Bestätigen-Button). `tone="danger"` färbt Badge + Bestätigen-Button rot.
 */
/** So lange nach dem Erscheinen schließt ein Klick daneben die Rückfrage nicht. */
export const KLICK_SPERRE_MS = 600;

export interface ConfirmDialogProps {
    open: boolean;
    title: string;
    /** Fließtext ODER beliebiger Inhalt (z. B. mit <b> hervorgehobenem Namen). */
    message: React.ReactNode;
    confirmLabel?: string;
    /** `null` blendet den Abbrechen-Knopf aus (reiner Hinweis mit „OK"). */
    cancelLabel?: string | null;
    tone?: "danger" | "primary";
    /** Sperrt die Buttons während einer laufenden Aktion (z. B. Löschen). */
    busy?: boolean;
    /** Symbol im Kopf; Standard ist der Papierkorb (Löschen-Rückfrage). */
    icon?: React.ReactNode;
    /** Stapelhöhe; Standard 100002 („über Fenstern"). */
    zIndex?: number;
    /**
     * Fokus beim Öffnen auf den Bestätigen-Knopf legen (Standard). `false`, wenn
     * der Inhalt selbst den Fokus braucht (z. B. ein Eingabefeld).
     */
    focusConfirm?: boolean;
    /**
     * Optionale dritte Handlung, links im Fuß (z. B. „Umkehren" beim
     * Verschmelzen, T-246). Schließt den Dialog nicht von selbst.
     */
    extraAction?: { label: string; onClick: () => void; title?: string };
    onConfirm: () => void;
    onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    message,
    confirmLabel = "Bestätigen",
    cancelLabel = "Abbrechen",
    tone = "danger",
    busy = false,
    icon,
    zIndex = 100002,
    focusConfirm = true,
    extraAction,
    onConfirm,
    onCancel,
}) => {
    const confirmRef = useRef<HTMLButtonElement | null>(null);
    // Wann die Rückfrage erschienen ist — siehe Hintergrund-Klick unten.
    const geoeffnetAm = useRef(0);
    useLayoutEffect(() => {
        if (open) geoeffnetAm.current = Date.now();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !busy) onCancel();
        };
        alleDokumente.addEventListener("keydown", onKey);
        // Fokus auf den Bestätigen-Button, sobald der Dialog erscheint.
        const t = focusConfirm ? window.setTimeout(() => confirmRef.current?.focus(), 0) : undefined;
        return () => {
            alleDokumente.removeEventListener("keydown", onKey);
            window.clearTimeout(t);
        };
    }, [open, busy, onCancel, focusConfirm]);

    if (!open) return null;

    const danger = tone === "danger";
    const accent = danger ? REFINED.err : REFINED.aub;
    const accentBg = danger ? REFINED.errBg : REFINED.aub50;

    return createPortal(
        <div
            role="presentation"
            onMouseDown={() => {
                // Ein Klick, der schon unterwegs war, als die Rückfrage
                // erschien (Doppelklick, schnelles Weiterklicken), ist kein
                // „Abbrechen“. Erscheint sie beim Verlassen eines Feldes,
                // schloss sie sich sonst sofort wieder — „der Hinweis Löschen
                // kam ganz kurz“ (T-464, im Browser nachgestellt).
                if (Date.now() - geoeffnetAm.current < KLICK_SPERRE_MS) return;
                if (!busy) onCancel();
            }}
            style={{
                position: "fixed",
                inset: 0,
                zIndex,
                background: "rgba(20,8,20,0.34)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
                fontFamily: REFINED.fontUI,
            }}
        >
            <div
                role="alertdialog"
                aria-modal="true"
                aria-label={title}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                    width: "min(420px, 100%)",
                    background: REFINED.white,
                    border: `1px solid ${REFINED.line}`,
                    borderRadius: 14,
                    boxShadow: REFINED.cardShadow,
                    overflow: "hidden",
                    color: REFINED.ink,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "15px 18px",
                        borderBottom: `2px solid ${REFINED.goldLine}`,
                    }}
                >
                    <span
                        style={{
                            width: 34,
                            height: 34,
                            flexShrink: 0,
                            borderRadius: 999,
                            background: accentBg,
                            color: accent,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {icon ?? I.trash}
                    </span>
                    <h3 style={{ margin: 0, fontSize: REFINED.fs.lg, fontWeight: 800 }}>{title}</h3>
                </div>
                <div style={{ padding: "16px 18px", fontSize: REFINED.fs.sm, color: REFINED.ink2, lineHeight: 1.5 }}>
                    {message}
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        padding: "12px 18px",
                        borderTop: `1px solid ${REFINED.line}`,
                        background: REFINED.fieldBg,
                    }}
                >
                    {extraAction && (
                        <button
                            type="button"
                            onClick={extraAction.onClick}
                            disabled={busy}
                            title={extraAction.title}
                            style={{
                                appearance: "none",
                                font: "inherit",
                                fontSize: REFINED.fs.sm,
                                fontWeight: 700,
                                padding: "8px 12px",
                                borderRadius: 10,
                                marginRight: "auto",
                                cursor: busy ? "not-allowed" : "pointer",
                                background: "transparent",
                                border: `1px dashed ${REFINED.aub100}`,
                                color: REFINED.aub,
                                opacity: busy ? 0.55 : 1,
                            }}
                        >
                            {extraAction.label}
                        </button>
                    )}
                    {cancelLabel !== null && (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        style={{
                            appearance: "none",
                            font: "inherit",
                            fontSize: REFINED.fs.sm,
                            fontWeight: 700,
                            padding: "8px 16px",
                            borderRadius: 10,
                            cursor: busy ? "not-allowed" : "pointer",
                            background: REFINED.white,
                            border: `1px solid ${REFINED.aub100}`,
                            color: REFINED.aub,
                            opacity: busy ? 0.55 : 1,
                        }}
                    >
                        {cancelLabel}
                    </button>
                    )}
                    <button
                        ref={confirmRef}
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        style={{
                            appearance: "none",
                            font: "inherit",
                            fontSize: REFINED.fs.sm,
                            fontWeight: 700,
                            padding: "8px 16px",
                            borderRadius: 10,
                            cursor: busy ? "not-allowed" : "pointer",
                            background: accent,
                            border: `1px solid ${accent}`,
                            color: REFINED.white,
                            opacity: busy ? 0.6 : 1,
                        }}
                    >
                        {busy ? "…" : confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default ConfirmDialog;
